import type { Node, Edge } from '@/types/node';
import type { NodeDefinition } from '@/types/api/nodeDefinition';

// ── Tool call argument types ─────────────────────────────────────────

export interface WorkflowNodeSpec {
  ref_id: string;
  node_type_id: string;
  display_name?: string;
  initial_data?: Record<string, unknown>;
}

export interface WorkflowEdgeSpec {
  source_ref_id: string;
  source_output_key: string;
  target_ref_id: string;
  target_input_key: string;
}

// ── Layout ───────────────────────────────────────────────────────────

const COLUMN_WIDTH = 550;
const PROMPT_NODE_HEIGHT = 350;
const MODEL_NODE_HEIGHT = 700;
const NODE_GAP = 40;

export function topologicalLayout(
  nodeSpecs: WorkflowNodeSpec[],
  edgeSpecs: WorkflowEdgeSpec[],
  existingNodes: Node[],
  nodeTypes?: NodeDefinition[],
): Map<string, { x: number; y: number }> {
  const refIds = new Set(nodeSpecs.map((n) => n.ref_id));

  const adj = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  for (const id of refIds) {
    adj.set(id, []);
    inDegree.set(id, 0);
  }
  for (const e of edgeSpecs) {
    if (!refIds.has(e.source_ref_id) || !refIds.has(e.target_ref_id)) continue;
    adj.get(e.source_ref_id)!.push(e.target_ref_id);
    inDegree.set(e.target_ref_id, (inDegree.get(e.target_ref_id) ?? 0) + 1);
  }

  const depth = new Map<string, number>();
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) {
      queue.push(id);
      depth.set(id, 0);
    }
  }

  let head = 0;
  while (head < queue.length) {
    const current = queue[head++];
    const currentDepth = depth.get(current) ?? 0;
    for (const neighbor of adj.get(current) ?? []) {
      const newDepth = currentDepth + 1;
      if (newDepth > (depth.get(neighbor) ?? 0)) {
        depth.set(neighbor, newDepth);
      }
      inDegree.set(neighbor, (inDegree.get(neighbor) ?? 0) - 1);
      if (inDegree.get(neighbor) === 0) {
        queue.push(neighbor);
      }
    }
  }

  for (const id of refIds) {
    if (!depth.has(id)) depth.set(id, 0);
  }

  let startX = 0;
  let startY = 0;
  if (existingNodes.length > 0) {
    let maxX = -Infinity;
    let minY = Infinity;
    for (const node of existingNodes) {
      const rightEdge = node.position.x + (node.width ?? 460);
      if (rightEdge > maxX) maxX = rightEdge;
      if (node.position.y < minY) minY = node.position.y;
    }
    startX = maxX + COLUMN_WIDTH;
    startY = minY;
  }

  const columnYOffset = new Map<number, number>();
  const positions = new Map<string, { x: number; y: number }>();

  for (const spec of nodeSpecs) {
    const col = depth.get(spec.ref_id) ?? 0;
    const currentY = columnYOffset.get(col) ?? 0;
    positions.set(spec.ref_id, {
      x: startX + col * COLUMN_WIDTH,
      y: startY + currentY,
    });
    const isModel = nodeTypes?.find((nt) => nt.id === spec.node_type_id)?.isModel ?? false;
    const nodeHeight = isModel ? MODEL_NODE_HEIGHT : PROMPT_NODE_HEIGHT;
    columnYOffset.set(col, currentY + nodeHeight + NODE_GAP);
  }

  return positions;
}

// ── Canvas summary ───────────────────────────────────────────────────

export function getCanvasSummary(nodes: Node[], edges: Edge[]): string {
  if (nodes.length === 0) return 'The canvas is empty.';

  const nodeLines = nodes.map((n) => {
    const typeName = n.type ?? 'unknown';
    const displayName = n.data?.name ?? typeName;
    return `- ${n.id}: ${displayName} (type: ${typeName})`;
  });

  const edgeLines = edges.map((e) => {
    const sourceName = nodes.find((n) => n.id === e.source)?.data?.name ?? e.source;
    const targetName = nodes.find((n) => n.id === e.target)?.data?.name ?? e.target;
    const sourceHandle = e.sourceHandle?.split('-output-')[1] ?? '?';
    const targetHandle = e.targetHandle?.split('-input-')[1] ?? '?';
    return `- ${sourceName}.${sourceHandle} → ${targetName}.${targetHandle}`;
  });

  let summary = `Nodes (${nodes.length}):\n${nodeLines.join('\n')}`;
  if (edgeLines.length > 0) {
    summary += `\n\nConnections (${edges.length}):\n${edgeLines.join('\n')}`;
  }
  return summary;
}

// ── initial_data whitelist ───────────────────────────────────────────

const ALLOWED_INITIAL_DATA_KEYS: Record<string, string[]> = {
  promptV3: ['prompt'],
  promptV2: ['prompt'],
  prompt_concat: ['prompt'],
  string: ['value', 'text'],
  boolean: ['value'],
  integer: ['value'],
  number_selector: ['value'],
};

export function sanitizeInitialData(
  nodeType: string,
  initialData: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!initialData) return undefined;
  const allowedKeys = ALLOWED_INITIAL_DATA_KEYS[nodeType];
  if (!allowedKeys) return undefined;
  const sanitized: Record<string, unknown> = {};
  let hasKeys = false;
  for (const key of allowedKeys) {
    if (key in initialData) {
      sanitized[key] = initialData[key];
      hasKeys = true;
    }
  }
  return hasKeys ? sanitized : undefined;
}
